import type { ShopperAgentOutreachCondition as ShopperAgentOutreachConditionType } from "@redotech/redo-flows-common/rules-v3/schemas";
import {
  SHOPPER_AGENT_OUTREACH_CONDITION_LABELS,
  SHOPPER_AGENT_OUTREACH_CONDITION_VALUES,
  type ShopperAgentOutreachConditionValue,
} from "@redotech/redo-flows-common/rules-v3/types";
import { useTranslation } from "@redotech/redo-i18n";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";

const outreachTypeItems: RedoListItem<ShopperAgentOutreachConditionValue>[] =
  SHOPPER_AGENT_OUTREACH_CONDITION_VALUES.map((value) => ({
    id: value,
    type: "text" as const,
    text: SHOPPER_AGENT_OUTREACH_CONDITION_LABELS[value],
    value,
  }));

export const ShopperAgentOutreachCondition = memo(
  function ShopperAgentOutreachCondition({
    condition,
    onChange,
    editableFields,
  }: {
    condition: ShopperAgentOutreachConditionType;
    onChange: (updated: ShopperAgentOutreachConditionType) => void;
    editableFields?: ReadonlySet<string>;
  }) {
    const { t } = useTranslation();
    const outreachTypesEditable =
      editableFields === undefined || editableFields.has("outreachTypes");

    const [dropdownButtonRef, setDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const selectedItems: RedoListItem<ShopperAgentOutreachConditionValue>[] =
      useMemo(() => {
        return (condition.outreachTypes || []).map((type) => ({
          id: type,
          type: "text" as const,
          text: SHOPPER_AGENT_OUTREACH_CONDITION_LABELS[type],
          value: type,
        }));
      }, [condition.outreachTypes]);

    const displayItem:
      | RedoListItem<ShopperAgentOutreachConditionValue>
      | undefined = useMemo(() => {
      if (selectedItems.length === 0) {
        return undefined;
      }
      if (selectedItems.length === 1) {
        return selectedItems[0];
      }
      return {
        id: "display",
        type: "text" as const,
        text: selectedItems
          .flatMap((item) => ("text" in item ? [item.text] : []))
          .join(", "),
        value: "none",
      };
    }, [selectedItems]);

    return (
      <Flex dir="column" gap="sm" w="full">
        <Text fontSize="xs" textColor="secondary">
          {t("Outreach type")}
        </Text>
        <RedoSingleSelectDropdownInputButton
          disabled={!outreachTypesEditable}
          isDropdownOpen={dropdownOpen}
          onClick={() =>
            outreachTypesEditable && setDropdownOpen(!dropdownOpen)
          }
          placeholder={t("Select outreach types")}
          ref={setDropdownButtonRef}
          selectedItem={displayItem}
          size="xs"
        />
        <RedoMultiselectDropdown
          dropdownAnchor={dropdownButtonRef}
          dropdownOpen={dropdownOpen}
          fitToAnchor
          items={outreachTypeItems}
          selectedItems={selectedItems}
          selectionVariant="checkbox"
          setDropdownOpen={setDropdownOpen}
          setSelectedItems={(items) => {
            if (!outreachTypesEditable) {
              return;
            }
            onChange({
              ...condition,
              outreachTypes: items.map((item) => item.value),
            });
          }}
          size="xs"
        />
      </Flex>
    );
  },
);
